import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { TimeFormat } from '@/core/stores/ui.store';
import { Button } from '@/shared/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

/**
 * Segmented time picker: typeable comboboxes for hour/minute, a plain `Select`
 * for AM/PM (only 2 options — search adds no value there).
 *
 * Restrictive by construction: the user can only commit an option present in
 * the list, so there is no free-text mask to parse and no way to end on a
 * malformed time. Typing filters the list instead of being validated char by
 * char. Honors the user's `timeFormat` for display (12h shows an AM/PM
 * segment, 24h does not) while always emitting the canonical `HH:mm` (24h)
 * `PlainTime` — the shape the backend and `PlannedDate` expect.
 *
 * Time of day is minute-precision: `HH:mm:ss` formats degrade to minutes,
 * which matches the `PlainTime` contract (`HH:mm`).
 */

const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));
const HOURS_24 = Array.from({ length: 24 }, (_, i) => pad(i));
const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1));

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** `HH:mm` (24h) → `{ hour, minute }`, or `null` when absent/malformed. */
function parseValue(value: string | null | undefined): { hour: number; minute: number } | null {
  if (!value || !/^\d{2}:\d{2}/.test(value)) return null;
  return { hour: Number(value.slice(0, 2)), minute: Number(value.slice(3, 5)) };
}

/** Convert a 12h display hour + period to a 24h hour. */
function to24Hour(displayHour: number, period: 'AM' | 'PM'): number {
  if (period === 'PM') return displayHour === 12 ? 12 : displayHour + 12;
  return displayHour === 12 ? 0 : displayHour;
}

interface TimeSegmentComboboxProps {
  options: string[];
  /** Raw option value currently selected (matches an entry in `options`). */
  value: string | undefined;
  label: (option: string) => string;
  onSelect: (option: string) => void;
  disabled?: boolean;
  testId?: string;
}

/** One typeable, searchable segment (hour or minute) backed by `Command`. */
function TimeSegmentCombobox({
  options,
  value,
  label,
  onSelect,
  disabled,
  testId,
}: TimeSegmentComboboxProps) {
  const [open, setOpen] = useState(false);

  return (
    // `modal` gives this popover its own scroll lock. The picker itself opens inside a
    // Dialog, whose scroll lock only allows scrolling inside the Dialog's own content —
    // a nested Popover's list would otherwise be dead to the mouse wheel. Radix's scroll
    // locks nest cooperatively, so this re-enables scrolling for this popover's content.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          data-testid={testId}
          className="h-7 w-14 justify-center px-2 text-xs font-normal"
        >
          {value != null ? label(value) : '--'}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-28 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          {/* Radix's default onOpenAutoFocus calls .focus() via its focus-scope, which
              scrolls the nearest scrollable ancestor to bring the input into view — jarring
              inside a scrolling list/table. Autofocus is prevented above and re-applied here
              via the plain HTML attribute instead, matching AsyncComboboxContent's fix. */}
          <CommandInput autoFocus className="h-8 text-xs" />
          <CommandList className="max-h-[220px]">
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
              —
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  value={label(option)}
                  keywords={[option]}
                  data-testid={testId ? `${testId}-option-${option}` : undefined}
                  onSelect={() => {
                    onSelect(option);
                    setOpen(false);
                  }}
                  className="justify-between text-xs"
                >
                  {label(option)}
                  {value === option && <Check className="size-3.5" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface TimeSelectProps {
  /** Canonical `HH:mm` (24h) value, or `null` when no time is set. */
  value: string | null;
  /** Emits the canonical `HH:mm` (24h) value. */
  onChange: (value: string) => void;
  timeFormat: TimeFormat;
  disabled?: boolean;
  className?: string;
  /** Base test id; the hour/minute/period triggers derive `${testId}-hour` etc. */
  testId?: string;
}

export function TimeSelect({
  value,
  onChange,
  timeFormat,
  disabled,
  className,
  testId,
}: TimeSelectProps) {
  const is12Hour = timeFormat.includes('a');
  const parsed = parseValue(value);

  const period: 'AM' | 'PM' = parsed && parsed.hour >= 12 ? 'PM' : 'AM';
  const displayHour = parsed ? parsed.hour % 12 || 12 : null;

  const hourValue = parsed ? (is12Hour ? String(displayHour) : pad(parsed.hour)) : undefined;
  const minuteValue = parsed ? pad(parsed.minute) : undefined;

  function emit(hour24: number, minute: number) {
    onChange(`${pad(hour24)}:${pad(minute)}`);
  }

  function handleHourSelect(next: string) {
    const minute = parsed?.minute ?? 0;
    const hour24 = is12Hour ? to24Hour(Number(next), period) : Number(next);
    emit(hour24, minute);
  }

  function handleMinuteSelect(next: string) {
    const hour24 = parsed?.hour ?? 0;
    emit(hour24, Number(next));
  }

  function handlePeriodChange(next: string) {
    const minute = parsed?.minute ?? 0;
    const hour24 = to24Hour(displayHour ?? 12, next as 'AM' | 'PM');
    emit(hour24, minute);
  }

  const hours = is12Hour ? HOURS_12 : HOURS_24;
  const hourLabel = (h: string) => (is12Hour ? pad(Number(h)) : h);
  const minuteLabel = (m: string) => m;

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <TimeSegmentCombobox
        options={hours}
        value={hourValue}
        label={hourLabel}
        onSelect={handleHourSelect}
        disabled={disabled}
        testId={testId ? `${testId}-hour` : undefined}
      />

      <span className="text-xs text-muted-foreground">:</span>

      <TimeSegmentCombobox
        options={MINUTES}
        value={minuteValue}
        label={minuteLabel}
        onSelect={handleMinuteSelect}
        disabled={disabled}
        testId={testId ? `${testId}-minute` : undefined}
      />

      {is12Hour && (
        <Select value={parsed ? period : ''} onValueChange={handlePeriodChange} disabled={disabled}>
          <SelectTrigger
            size="sm"
            className="h-7 w-16 px-2 text-xs"
            data-testid={testId ? `${testId}-period` : undefined}
          >
            <SelectValue placeholder="--" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM" className="text-xs">
              AM
            </SelectItem>
            <SelectItem value="PM" className="text-xs">
              PM
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
